/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import { useCustomToast } from '@/hooks/use-custom-toast';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { memo, useCallback, useState, useMemo } from 'react';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { handleDomBubbleEvent } from '@/utils/dom';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import { Spinner } from '@/components/_ui/spinner';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import {
  sendCredentialAppStoreChannelValidationSchema,
  TSendCredentialAppStoreChannelValidation,
  useSendCredentialAppStoreChannelMutation,
} from '../../../api/send-credential-app-store-channel.api';

const TOAST_MESSAGES_SEND_CREDENTIAL_APP_STORE = {
  success: 'App store credential sent successfully, please select app store channel!',
  error: 'Failed to send app store credential',
};

export type TSendCredentialAppStoreChannelModalType = 'SendCredentialAppStoreChannelModal';
export interface ISendCredentialAppStoreChannelModalProps {}

const SendCredentialAppStoreModal = memo(({}: ISendCredentialAppStoreChannelModalProps) => {
  const showToast = useCustomToast();
  const channelModalRootActions = useChannelModalRootActionsStore();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const isOpenSendCredentialAppStoreChannelModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('SendCredentialAppStoreChannelModal');
  }, [channelModalRootActions]);

  const form = useForm<TSendCredentialAppStoreChannelValidation>({
    mode: 'onChange',
    resolver: zodResolver(sendCredentialAppStoreChannelValidationSchema),
  });

  const mutateSendCredentialAppStoreChannelMutation = useSendCredentialAppStoreChannelMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_SEND_CREDENTIAL_APP_STORE.error;

        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: (data) => {
        const appListAppStore = data.payload;
        channelModalRootActions.openModal('SelectConnectAppStoreChannelModal', {
          listAppStore: appListAppStore,
        });
        channelModalRootActions.closeModal('SendCredentialAppStoreChannelModal');
        showToast({
          message: TOAST_MESSAGES_SEND_CREDENTIAL_APP_STORE.success,
          type: 'success',
        });
      },
    },
  });

  const onSubmit = useCallback(
    (data: TSendCredentialAppStoreChannelValidation) => {
      if (!data.issuer_id || !data.key_id || !selectedFile) {
        showToast({
          type: 'error',
          message: 'Please enter issuer ID, API key ID and select a key file',
        });
        return;
      }
      mutateSendCredentialAppStoreChannelMutation.mutate({
        issuer_id: data.issuer_id,
        key_id: data.key_id,
        privateKeyFile: selectedFile,
      });
    },
    [mutateSendCredentialAppStoreChannelMutation, showToast, selectedFile]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        form.setValue('privateKeyFile', file);
      }
    },
    [form]
  );

  const truncateFileName = useCallback((fileName: string, maxLength: number = 30) => {
    if (fileName.length <= maxLength) {
      return fileName;
    }
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4) + '...';
    return `${truncatedName}.${extension}`;
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.name.endsWith('.p8')) {
          setSelectedFile(file);
          form.setValue('privateKeyFile', file);
        } else {
          showToast({
            type: 'error',
            message: 'Please select a .p8 file',
          });
        }
      }
    },
    [form, showToast]
  );

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('SendCredentialAppStoreChannelModal');
    form.reset();
  }, [channelModalRootActions, form]);

  return (
    <Modal
      isOpen={isOpenSendCredentialAppStoreChannelModal}
      onClose={handleClose}
      isCloseOutside={false}
      className="xl:w-[480px] w-[90vw] p-[32px]"
      lockBodyScroll
    >
      <ModalHeader>
        <ModalTitle>Please Insert your App Store Information</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(onSubmit)(e);
          }}
          id="send-credential-app-store-form"
          className="gap-[12px] h-[300px] flex flex-col min-h-0"
        >
          {/* Issuer ID Field */}
          <div className="flex flex-col gap-1">
            <label className="text-[14px] font-normal text-[#25151E]">Issuer ID</label>
            <Input
              id="issuer-id"
              placeholder="Issuer ID (e.g. 12345678-90ab-cdef-1234-567890abcdef)"
              {...form.register('issuer_id')}
              className="w-full"
            />
            {form.formState.errors.issuer_id && (
              <div className="text-[12px] font-normal text-[#CC0202]">{form.formState.errors.issuer_id.message}</div>
            )}
          </div>

          {/* API Key ID Field */}
          <div className="flex flex-col gap-1">
            <label className="text-[14px] font-normal text-[#25151E]">API Key ID</label>
            <Input
              id="api-key-id"
              placeholder="API Key ID (e.g. ABCDE12345)"
              {...form.register('key_id')}
              className="w-full"
            />
            {form.formState.errors.key_id && (
              <div className="text-[12px] font-normal text-[#CC0202]">{form.formState.errors.key_id.message}</div>
            )}
          </div>

          {/* Key File Upload Field */}
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-[14px] font-normal text-[#25151E]">Private Key File (.p8)</label>
            <div className="relative flex-1 min-h-0">
              <input type="file" id="key-file" accept=".p8" onChange={handleFileChange} className="hidden" />
              <label
                htmlFor="key-file"
                className={cn(
                  'flex flex-1 items-center justify-center gap-2 w-full h-full min-h-[120px] border rounded-[8px] cursor-pointer transition-colors',
                  isDragOver ? 'border-[#0C8DBF] bg-[#0C8DBF]/5' : 'border-[#D9D9D9] hover:bg-[#F8F9FA]'
                )}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center gap-[6px]">
                  {selectedFile ? (
                    <>
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex flex-col items-center gap-[2px]">
                        <span className="text-[14px] text-[#059669] text-center font-medium" title={selectedFile.name}>
                          {truncateFileName(selectedFile.name)}
                        </span>
                        <span className="text-[12px] text-[#6B7280] text-center">File selected successfully</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-12 bg-[#0C8DBF]/10 rounded-full flex items-center justify-center">
                        <svg
                          className="flex-shrink-0"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 26 26"
                          fill="none"
                        >
                          <path
                            d="M16.2497 2.16602H6.49967C5.92504 2.16602 5.37394 2.39429 4.96761 2.80062C4.56128 3.20695 4.33301 3.75805 4.33301 4.33268V21.666C4.33301 22.2406 4.56128 22.7917 4.96761 23.1981C5.37394 23.6044 5.92504 23.8327 6.49967 23.8327H19.4997C20.0743 23.8327 20.6254 23.6044 21.0317 23.1981C21.4381 22.7917 21.6663 22.2406 21.6663 21.666V7.58268L16.2497 2.16602Z"
                            stroke="#0C8DBF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M15.167 2.16602V6.49935C15.167 7.07398 15.3953 7.62509 15.8016 8.03141C16.2079 8.43774 16.759 8.66602 17.3337 8.66602H21.667"
                            stroke="#0C8DBF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M13 13V19.5"
                            stroke="#0C8DBF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16.25 16.25L13 13L9.75 16.25"
                            stroke="#0C8DBF"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>

                      <span className="text-[12px] text-[#9CA3AF] text-center">
                        {isDragOver ? 'Drop your .p8 file here' : 'Click to browse or drag and drop your .p8 file here'}
                      </span>
                    </>
                  )}
                </div>
              </label>
            </div>
            {form.formState.errors.privateKeyFile && (
              <div className="text-[12px] font-normal text-[#CC0202]">
                {String(form.formState.errors.privateKeyFile?.message || 'Key file is required')}
              </div>
            )}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton
          form="send-credential-app-store-form"
          type="submit"
          disabled={mutateSendCredentialAppStoreChannelMutation.isPending}
        >
          {mutateSendCredentialAppStoreChannelMutation.isPending ? (
            <Spinner isLoading={true} className="w-[18px] h-[18px]" />
          ) : null}{' '}
          Submit
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

SendCredentialAppStoreModal.displayName = 'SendCredentialAppStoreModal';
export default SendCredentialAppStoreModal;
